// src/sectors/pilates/sessions/components/calendar/DayCell.jsx
import React, { useMemo, useState, useCallback } from 'react';
import Icon from '@mui/material/Icon';
import Tooltip from '@mui/material/Tooltip';
import { useTheme as useMuiTheme, alpha } from '@mui/material/styles';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import { SoftSessionCard } from '../SoftSessionCard';

/* ---------------- utils (local) ---------------- */
const toStr = (x) => (x == null ? '' : String(x));
const asInt = (x) => {
  const n = Number(x);
  return Number.isFinite(n) ? n : null;
};
const safeId = (s, dayISO, startMin, idx) =>
  toStr(s?.id) ||
  toStr(s?.sessionId) ||
  toStr(s?.uuid) ||
  `${toStr(dayISO)}|${toStr(startMin)}|${toStr(s?.trainerId)}|${idx}`;

// Takvim + kartlar için tek standarda normalize
function normalizeSession(s, tr) {
  const cap = Number.isFinite(s?.capacity) ? s.capacity : Number.isFinite(s?.cap) ? s.cap : 1;

  const participants = Array.isArray(s?.participants)
    ? s.participants
    : Array.isArray(s?.participantsPreview)
    ? s.participantsPreview.map((name) => ({ memberNameSnapshot: name }))
    : [];

  const booked = Number.isFinite(s?.participantCount)
    ? s.participantCount
    : Number.isFinite(s?.booked)
    ? s.booked
    : participants.length;

  return {
    ...s,
    participants, // ✅ garanti

    activityName: s?.activityName || s?.activityNameSnapshot || s?.title || 'Seans',
    sessionType: s?.sessionType || s?.type || 'NORMAL',

    trainerId: s?.trainerId,
    trainerName:
      (s?.trainerName && s.trainerName !== 'Unknown' ? s.trainerName : '') || tr?.name || '—',

    capacity: cap,
    participantCount: booked,
  };
}

export function DayCell({
  dayISO,
  startMin,
  colIdx,
  isToday,
  list,
  trainersById,
  copyMode = false,
  onSessionClick,
  onSessionEdit,
  onSessionDelete,
  attendanceMode,
  onAttendanceClick,
  editMode,
  onEmptyClick,
  ghostList = [],
  ghostMode = false,
  filterTrainerId = 'ALL',
  onMoveSession,
  moveMode,
  setMoveMode,
  moveFrom,
  setMoveFrom,
  minCellHeight = 64,
  cellPad = 6,
  cellGap = 6,
}) {
  const theme = useMuiTheme();
  const isWeekend = colIdx >= 5;
  const isMoveTarget =
    Boolean(moveMode && moveFrom) &&
    String(moveFrom?.dayISO) === String(dayISO) &&
    Number(moveFrom?.startMin) === Number(startMin);
  const [moveHoverTarget, setMoveHoverTarget] = useState(null);
  const viewMode = String(filterTrainerId) === 'ALL' ? 'ALL' : 'TRAINER';

  // ✅ hocaya özel görünümde: o slotta bu hocanın seansı var mı?
  const isOccupiedForThisTrainer = useMemo(() => {
    if (viewMode === 'ALL') return false;
    return (list || []).some((s) => String(s?.trainerId) === String(filterTrainerId));
  }, [list, viewMode, filterTrainerId]);

  // ✅ edit + trainer view kuralı
  const canAdd = Boolean(
    editMode && !moveMode && (viewMode === 'ALL' ? true : !isOccupiedForThisTrainer)
  );

  const handleAdd = useCallback(
    (e) => {
      e?.stopPropagation?.();
      e?.preventDefault?.();
      if (!canAdd) return;

      onEmptyClick?.({
        dayISO,
        startMin,
        defaultTrainerId: viewMode === 'ALL' ? 'ALL' : String(filterTrainerId),
      });
    },
    [canAdd, onEmptyClick, dayISO, startMin, viewMode, filterTrainerId]
  );

  // DROP highlight
  const [isDragOver, setIsDragOver] = useState(false);
  const canDropHere = Boolean(editMode);

  const handleDragOver = (e) => {
    if (!canDropHere) return;
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e) => {
    if (!canDropHere) return;
    e.preventDefault();
    setIsDragOver(false);

    try {
      const raw = e.dataTransfer.getData('application/x-solisprox-session');
      if (!raw) return;
      const payload = JSON.parse(raw);

      const sessionId = payload?.sessionId;
      const fromDay = payload?.from?.dayISO;
      const fromMin = payload?.from?.startMin;

      if (!sessionId || !fromDay || typeof fromMin !== 'number') return;
      if (String(fromDay) === String(dayISO) && Number(fromMin) === Number(startMin)) return;

      onMoveSession?.({
        sessionId,
        from: { dayISO: fromDay, startMin: fromMin },
        to: { dayISO, startMin },
      });
    } catch {
      // ignore
    }
  };

  // ✅ hover action row: taşma olmasın, her zaman satır içinde kalsın
  const actionRowSx = {
    mt: 0.4,
    px: 0.2,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 0.9,
    opacity: 0,
    transform: 'translateY(-2px)',
    transition: 'opacity 140ms ease, transform 140ms ease',
    '.SessionWrap:hover &': { opacity: 1, transform: 'translateY(0)' },
    minWidth: 0,
  };

  const actionBtnSx = (tone) => {
    const isDel = tone === 'delete';
    const base = isDel ? '#dc2626' : '#334155';
    const hover = isDel ? '#b91c1c' : '#0f172a';
    return {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.35,
      cursor: 'pointer',
      userSelect: 'none',
      color: base,
      whiteSpace: 'nowrap',
      '&:hover': { color: hover },
    };
  };

  // ✅ list'i bir kez normalize et (render içinde tekrar tekrar iş yapma)
  const normalizedList = useMemo(() => {
    const arr = Array.isArray(list) ? list : [];
    return arr.map((s) => {
      const tr = trainersById?.get?.(String(s?.trainerId));
      return normalizeSession(s, tr);
    });
  }, [list, trainersById]);

  // ✅ ghost (preview) list'i normalize et
  const normalizedGhostList = useMemo(() => {
    const arr = Array.isArray(ghostList) ? ghostList : [];
    return arr.map((s) => {
      const tr = trainersById?.get?.(String(s?.trainerId));
      return normalizeSession(s, tr);
    });
  }, [ghostList, trainersById]);

  const hasReal = normalizedList.length > 0;
  const showGhost = Boolean(ghostMode && !hasReal && normalizedGhostList.length > 0);

  const handleCellClick = (e) => {
    if (!moveMode || !moveFrom) return;

    e?.stopPropagation?.();
    e?.preventDefault?.();

    const fromDay = String(moveFrom.dayISO);
    const fromMin = Number(moveFrom.startMin);
    const toDay = String(dayISO);
    const toMin = Number(startMin);

    // aynı hücreye taşıma yok
    if (fromDay === toDay && fromMin === toMin) return;

    // ✅ ÖNCE move mode kapat (aynı event zincirinde tekrar tetiklenmesin)
    setMoveMode?.(false);
    setMoveFrom?.(null);

    // ✅ highlight (opsiyonel)
    setMoveHoverTarget({ dayISO: toDay, startMin: toMin });

    // ✅ SONRA onMoveSession çağır
    onMoveSession?.({
      sessionId: moveFrom.sessionId,
      from: { dayISO: fromDay, startMin: fromMin },
      to: { dayISO: toDay, startMin: toMin },
    });

    // ✅ highlight temizle (0ms yerine küçük gecikme daha stabil)
    setTimeout(() => setMoveHoverTarget(null), 150);
  };

  const isMoveHover =
    Boolean(moveMode && moveFrom && moveHoverTarget) &&
    String(moveHoverTarget.dayISO) === String(dayISO) &&
    Number(moveHoverTarget.startMin) === Number(startMin);

  const isMoveSource =
    Boolean(moveMode && moveFrom) &&
    String(moveFrom.dayISO) === String(dayISO) &&
    Number(moveFrom.startMin) === Number(startMin);
  const showGreen = isDragOver || isMoveHover; // tek kaynak

  return (
    <MDBox
      className="DayCellRoot"
      data-dayiso={dayISO}
      data-startmin={startMin}
      onClick={moveMode && moveFrom && !copyMode ? handleCellClick : undefined}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        position: 'relative',
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderRight: colIdx === 6 ? 'none' : `1px solid ${theme.palette.divider}`,
        minHeight: minCellHeight,
        height: 'auto',

        background: isToday
          ? alpha(theme.palette.primary.main, 0.05)
          : isWeekend
          ? alpha('#000', 0.02)
          : 'transparent',

        // ✅ SOURCE hücre hafif mavi
        outline: isMoveSource ? '2px solid #3b82f6' : showGreen ? '2px solid #16a34a' : 'none',
        outlineOffset: isMoveSource || showGreen ? '-2px' : 0,
        boxShadow: isMoveSource
          ? 'inset 0 0 0 999px rgba(59,130,246,0.06)'
          : showGreen
          ? 'inset 0 0 0 999px rgba(22,163,74,0.06)'
          : 'none',

        p: `${cellPad}px`,
        boxSizing: 'border-box',
        overflow: 'visible',
        minWidth: 0,
      }}
    >
      {/* + button (editMode) */}
      {editMode ? (
        <MDBox
          sx={{
            position: 'absolute',
            top: 6,
            right: 6,
            zIndex: 40,
            opacity: 0,
            transform: 'translateY(-2px)',
            transition: 'opacity 140ms ease, transform 140ms ease',
            '.DayCellRoot:hover &': { opacity: 1, transform: 'translateY(0)' },
          }}
        >
          <Tooltip title={canAdd ? 'Seans ekle' : 'Bu slot dolu'} arrow>
            <MDBox
              onClick={handleAdd}
              sx={{
                width: 28,
                height: 28,
                borderRadius: 999,
                display: 'grid',
                placeItems: 'center',
                cursor: canAdd ? 'pointer' : 'not-allowed',
                background: canAdd ? '#16a34a' : alpha('#cbd5e1', 0.9),
                border: `2px solid ${canAdd ? '#15803d' : alpha('#94a3b8', 0.8)}`,
                boxShadow: canAdd ? '0 10px 22px rgba(22,163,74,0.40)' : 'none',
                '&:hover': canAdd
                  ? {
                      background: '#15803d',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 14px 26px rgba(22,163,74,0.48)',
                    }
                  : undefined,
                '&:active': canAdd ? { transform: 'translateY(0) scale(0.96)' } : undefined,
                transition: '140ms ease',
              }}
            >
              <Icon sx={{ fontSize: 20, color: '#fff', lineHeight: 1, fontWeight: 900 }}>add</Icon>
            </MDBox>
          </Tooltip>
        </MDBox>
      ) : null}

      {/* sessions list */}
      {normalizedList.length ? (
        <MDBox
          sx={{
            display: 'grid',
            gap: `${cellGap}px`,
            alignContent: 'start',
            minWidth: 0,
          }}
        >
          {normalizedList.map((s, idx) => {
            const key = safeId(s, dayISO, startMin, idx);

            return (
              <MDBox
                key={key}
                className="SessionWrap"
                sx={{ display: 'grid', gap: 0.2, minWidth: 0 }}
              >
                {/* drag wrapper */}
                <MDBox
                  draggable={Boolean(editMode && s?.id)}
                  onDragStart={(e) => {
                    if (!editMode) return;
                    const sid = s?.id;
                    if (!sid) return;
                    const payload = {
                      sessionId: sid,
                      from: { dayISO, startMin: asInt(startMin) ?? 0 },
                    };
                    e.dataTransfer.setData(
                      'application/x-solisprox-session',
                      JSON.stringify(payload)
                    );
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  sx={{
                    cursor: editMode ? 'grab' : 'pointer',
                    '&:active': editMode ? { cursor: 'grabbing' } : undefined,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <SoftSessionCard
                    s={s}
                    editMode={Boolean(editMode)} // ✅ BUNU EKLE (kritik)
                    forceClickable={Boolean(copyMode)} // ✅ KRİTİK: copyMode'da tıklama her zaman aktif
                    selected={false} // (opsiyonel: source id geçirirsen true yapacağız)
                    onClick={(e) => {
                      e?.stopPropagation?.();
                      // ✅ COPY MODE: seansı seçtirme modu
                      if (copyMode) return onSessionClick?.(s);
                      if (attendanceMode) return onAttendanceClick?.(s);
                      onSessionClick?.(s);
                    }}
                    onEdit={(x) => onSessionEdit?.(x)}
                    onDelete={(x) => onSessionDelete?.(x)}
                    copyMode={Boolean(copyMode)}
                    onCopy={(s) => startCopySession(s)} // ✅
                    onMove={() => {
                      // burada senin “Taşı modu”nu başlatan state’i set edeceksin
                      setMoveMode?.(true);
                      setMoveFrom?.({
                        sessionId: String(s.id),
                        dayISO,
                        startMin: Number(startMin) || 0,
                      });
                    }}
                  />
                </MDBox>
              </MDBox>
            );
          })}
        </MDBox>
      ) : null}
      {/* ✅ GHOST (preview) */}
      {showGhost ? (
        <MDBox
          sx={{
            display: 'grid',
            gap: `${cellGap}px`,
            alignContent: 'start',
            minWidth: 0,
            opacity: 0.55,
            filter: 'grayscale(0.2)',
          }}
        >
          {normalizedGhostList.map((s, idx) => {
            const key = `ghost-${safeId(s, dayISO, startMin, idx)}`;
            return (
              <MDBox key={key} sx={{ minWidth: 0, pointerEvents: 'none' }}>
                <SoftSessionCard
                  s={s}
                  editMode={false}
                  forceClickable={false}
                  copyMode={false}
                  // ghost görünümü: kart içine küçük bir badge hissi
                  ghost
                />
                <MDTypography
                  variant="caption"
                  sx={{
                    mt: 0.35,
                    display: 'inline-block',
                    px: 0.9,
                    py: 0.25,
                    borderRadius: 999,
                    fontWeight: 900,
                    fontSize: 11,
                    letterSpacing: 0.2,
                    color: 'rgb(180, 83, 9)',
                    background: 'rgba(245, 158, 11, 0.14)',
                    border: '1px dashed rgba(245, 158, 11, 0.45)',
                  }}
                >
                  Önizleme
                </MDTypography>
              </MDBox>
            );
          })}
        </MDBox>
      ) : null}
    </MDBox>
  );
}
